import { ControlledAccordion } from '@components/Accordion/Accordion';
// hooks and context
import { SideEditorScrollType } from '@components/SideEditor/SideEditor';
import { useSideEditorContext } from '@components/SideEditor/utils';
import { emailSignatureSideEditorServicesContext } from '@context/email-signature-services-context';
import useNode from '@hooks/use-node';
import { generateNewEmailSignatureContactFieldId } from '@modules/email-signatures-helpers';
import Emitter from '@modules/event-emitter';
// utils
import { generateEntityId } from '@modules/helpers';
// es-toolkit/compat: lodash isEmpty treats 0/true/null as empty
import { isEmpty } from 'es-toolkit/compat';
import {
  type FC,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

// consts and types
import { CONTACT_FIELD_ICON_OPTIONS, EmailSignatureContactDetailsFieldWidth } from '../../consts';
import {
  type EmailSignatureContactDetailsFieldId,
  EmailSignatureContactDetailsFieldTypes,
  SideEditorNodeKey,
} from '../../types';
import EmailSignatureEditorNewFieldButton from '../EmailSignatureEditorNewFieldButton';
import EmailSignatureEditorPanelNewFieldSection from '../EmailSignatureEditorPanelNewFieldsSection';
import EmailSignatureEditorPanelSection, {
  EmailSignatureEditorEmptyPanelSection,
  EmailSignatureEditorPanelSectionContents,
  EmailSignatureEditorPanelSectionHeading,
} from '../EmailSignatureEditorPanelSection';
import EmailSignatureLockLayer from '../EmailSignatureLockLayer';
// components
import EmailSignatureEditableAccordionItem from './EmailSignatureEditableContactDetailsAccordionItem';
import styles from './EmailSignatureEditorContactSection.module.scss';

interface EmailSignatureEditorContactAccordionContextValues {
  isInSwapMode: boolean;
  currentFieldIdToSwap: EmailSignatureContactDetailsFieldId | null;
  onSwapActivate: (fieldIdOfFieldToSwap: EmailSignatureContactDetailsFieldId) => void;
  onEditActivate: (fieldId: EmailSignatureContactDetailsFieldId) => void;
  onDeleteActivate: (fieldId: EmailSignatureContactDetailsFieldId) => void;
  toggleItem: (toggledItem: number) => void;
}

export const EmailSignatureEditorContactAccordionContext =
  createContext<EmailSignatureEditorContactAccordionContextValues>({
    currentFieldIdToSwap: null,
    isInSwapMode: false,
    onSwapActivate: () => console.log('Warning! onSwapActivate is not implemented'),
    onEditActivate: () => console.log('Warning! onEditActivate is not implemented'),
    onDeleteActivate: () => console.log('Warning! onDeleteActivate is not implemented'),
    toggleItem: () => console.log('Warning! toggleItem is not implemented'),
  });

const EmailSignatureSideEditorContactSection: FC<React.PropsWithChildren<unknown>> = () => {
  // This accordionKey is used to force react to rerender the list of accordion items whenever
  // the users delete or add extra field, this is a fix to the reach ui's bug where the indices
  // of the items are shuffled whenever the items are changed
  const [accordionKey, setAccordionKey] = useState(generateEntityId());
  const { emailSignature, emailSignatureDispatch, isInTransitionState, isSignatureLocked } =
    useContext(emailSignatureSideEditorServicesContext);
  const [openedEditingPanels, setOpenedEditingPanels] = useState<number[]>([]);
  const [currentFieldIdToSwap, setCurrentFieldIdToSwap] =
    useState<EmailSignatureContactDetailsFieldId | null>(null);
  const [newFieldIconsSectionDiv, newFieldIconsSectionDivRef] = useNode<HTMLDivElement>();
  const [editableDetailsSectionDiv, editableDetailsAccordionDivRef] = useNode<HTMLDivElement>();
  const { scrollIntoView, panelIndex, registerNode } = useSideEditorContext();

  const isInSwapMode = useMemo(() => currentFieldIdToSwap !== null, [currentFieldIdToSwap]);

  const onSwapActivate = (fieldIdOfFieldToSwap: EmailSignatureContactDetailsFieldId) => {
    if (fieldIdOfFieldToSwap !== currentFieldIdToSwap && currentFieldIdToSwap !== null) {
      emailSignatureDispatch({
        type: 'SWAP_CONTACT_DETAIL_FIELDs',
        value: { fieldIdA: currentFieldIdToSwap, fieldIdB: fieldIdOfFieldToSwap },
      });
      setCurrentFieldIdToSwap(null);

      // When we swap the accordions positions, the indices are disrupted and reach ui
      // is not smart enough to adjust the indices, we have to change to the accordion key
      // to force rerender. But if we set the key immediately, the swap animation
      // will not be rendered so we have to wait for a while to set the key
      setTimeout(() => setAccordionKey(generateEntityId()), 500);
    } else if (fieldIdOfFieldToSwap === currentFieldIdToSwap && currentFieldIdToSwap !== null) {
      // when the users click the swap icon of the same accordion, exit the swap mode
      setCurrentFieldIdToSwap(null);
    } else if (currentFieldIdToSwap === null) {
      // when the users click the swap icon, close all accordions and enter swap mode
      setOpenedEditingPanels([]);
      setCurrentFieldIdToSwap(fieldIdOfFieldToSwap);
    }
  };

  const onEditActivate = (fieldId: EmailSignatureContactDetailsFieldId) => {
    // when the users click the edit icon, open the accordion
    const indexOfField = emailSignature.contactDetailsOrder.findIndex((id) => id === fieldId);

    toggleItem(indexOfField);
  };

  const onDeleteActivate = (fieldId: EmailSignatureContactDetailsFieldId) => {
    const indexOfField = emailSignature.contactDetailsOrder.findIndex((id) => id === fieldId);
    // regenerate the accordion key to force reach ui to rerender to avoid
    // incorrect indices
    setAccordionKey(generateEntityId());

    // decrement the indices of the opened accordions below the deleted accordion
    setOpenedEditingPanels((oldOpenedEditingPanels) => {
      const incrementedOpenedPanels = oldOpenedEditingPanels
        .filter((currentIndex) => currentIndex > indexOfField)
        .map((panel) => Math.max(panel - 1, 0));
      return [...incrementedOpenedPanels];
    });

    emailSignatureDispatch({ type: 'DELETE_CONTACT_DETAIL_FIELD', value: fieldId });
  };

  function toggleItem(toggledItem: number) {
    if (openedEditingPanels.includes(toggledItem)) {
      setOpenedEditingPanels(
        openedEditingPanels.filter((currentIndex) => currentIndex !== toggledItem),
      );
    } else {
      setOpenedEditingPanels([...openedEditingPanels, toggledItem].sort());
    }
    setCurrentFieldIdToSwap(null);
  }

  useEffect(() => {
    const scrollEventHandler = (fieldId?: string) => {
      if (isInTransitionState) {
        return;
      }
      if (isEmpty(emailSignature.contactDetails)) {
        scrollIntoView(SideEditorNodeKey.CONTACT_DETAILS_NODE, SideEditorScrollType.START);
        return;
      }

      const indexOfField = emailSignature.contactDetailsOrder.findIndex((id) => id === fieldId);
      setAccordionKey(generateEntityId());
      setOpenedEditingPanels([indexOfField]);

      // Scroll in the next event loop after the accordion item is open
      // scroll the corresponding accordion with the given fieldId to the top
      fieldId &&
        setTimeout(
          () =>
            scrollIntoView(
              `CONTACT_DETAIL_ACCORDION_${fieldId}`,
              SideEditorScrollType.START,
              false,
            ),
          100,
        );
      // if fieldId is not given, scroll the whole section to the top
      !fieldId &&
        scrollIntoView(SideEditorNodeKey.CONTACT_DETAILS_NODE, SideEditorScrollType.START);
    };
    Emitter.on('SCROLL_TO_CONTACT_DETAIL', scrollEventHandler);

    return () => {
      Emitter.off('SCROLL_TO_CONTACT_DETAIL', scrollEventHandler);
    };
  }, [
    emailSignature.contactDetailsOrder,
    emailSignature.contactDetails,
    isInTransitionState,
    scrollIntoView,
  ]);

  useEffect(() => {
    if (!newFieldIconsSectionDiv) return;
    registerNode(SideEditorNodeKey.NEW_CONTACT_DETAILS_NODE, {
      panelBelongTo: panelIndex,
      node: newFieldIconsSectionDiv,
    });
  }, [newFieldIconsSectionDiv, panelIndex, registerNode]);

  useEffect(() => {
    if (!editableDetailsSectionDiv) return;
    registerNode(SideEditorNodeKey.CONTACT_DETAILS_NODE, {
      panelBelongTo: panelIndex,
      node: editableDetailsSectionDiv,
    });
  }, [editableDetailsSectionDiv, panelIndex, registerNode]);

  const handleAddNewField = useCallback(
    (fieldType: EmailSignatureContactDetailsFieldTypes) => {
      const newContactFieldId = generateNewEmailSignatureContactFieldId();

      emailSignatureDispatch({
        type: 'ADD_CONTACT_DETAIL_FIELD',
        value: {
          id: newContactFieldId,
          type: fieldType,
          label: '',
          value: '',
          baseIcon: CONTACT_FIELD_ICON_OPTIONS[fieldType][0],
          fieldWidth: EmailSignatureContactDetailsFieldWidth.HALF_WIDTH,
          iconUrl: '',
        },
      });
      setAccordionKey(generateEntityId());
      setOpenedEditingPanels([emailSignature.contactDetailsOrder.length]);
      // set a timeout because we need to wait for the next cycle when the new field is rendered
      setTimeout(() => {
        scrollIntoView(
          `CONTACT_DETAIL_ACCORDION_${newContactFieldId}`,
          SideEditorScrollType.START,
          false,
        );
      }, 100);
    },
    [emailSignature.contactDetailsOrder.length, emailSignatureDispatch, scrollIntoView],
  );

  return (
    <>
      {emailSignature.contactDetailsOrder.length > 0 && (
        <EmailSignatureEditorPanelSection ref={editableDetailsAccordionDivRef}>
          <EmailSignatureEditorPanelSectionHeading heading="Details" />
          <EmailSignatureEditorPanelSectionContents>
            <EmailSignatureEditorContactAccordionContext.Provider
              value={{
                toggleItem,
                currentFieldIdToSwap,
                isInSwapMode,
                onSwapActivate,
                onEditActivate,
                onDeleteActivate,
              }}
            >
              <ControlledAccordion index={openedEditingPanels} onChange={toggleItem}>
                <div key={accordionKey}>
                  {emailSignature.contactDetailsOrder.map((contactDetailFieldId) => (
                    <EmailSignatureEditableAccordionItem
                      isLocked={isSignatureLocked}
                      key={contactDetailFieldId}
                      {...emailSignature.contactDetails[contactDetailFieldId]}
                    />
                  ))}
                </div>
              </ControlledAccordion>
            </EmailSignatureEditorContactAccordionContext.Provider>
          </EmailSignatureEditorPanelSectionContents>
        </EmailSignatureEditorPanelSection>
      )}
      {emailSignature.contactDetailsOrder.length === 0 && (
        <EmailSignatureEditorEmptyPanelSection ref={editableDetailsAccordionDivRef}>
          <EmailSignatureEditorPanelSectionHeading heading="Details" />
          <EmailSignatureEditorPanelSectionContents>
            <div className={styles['add-contact-fields-message']}>
              Click the buttons below to add new details fields
            </div>
          </EmailSignatureEditorPanelSectionContents>
        </EmailSignatureEditorEmptyPanelSection>
      )}
      <div
        className={styles['new-contact-fields-section']}
        data-sticky={!isEmpty(emailSignature.contactDetails)}
        ref={newFieldIconsSectionDivRef}
      >
        <div className={styles['new-contact-fields-section-wrapper']}>
          <EmailSignatureEditorPanelNewFieldSection ref={newFieldIconsSectionDivRef}>
            <EmailSignatureEditorNewFieldButton
              disabled={isSignatureLocked}
              onActivate={handleAddNewField}
              type={EmailSignatureContactDetailsFieldTypes.PHONE}
              subtitle="Phone"
            />

            <EmailSignatureEditorNewFieldButton
              disabled={isSignatureLocked}
              onActivate={handleAddNewField}
              type={EmailSignatureContactDetailsFieldTypes.EMAIL}
              subtitle="Email"
            />

            <EmailSignatureEditorNewFieldButton
              disabled={isSignatureLocked}
              onActivate={handleAddNewField}
              type={EmailSignatureContactDetailsFieldTypes.WEBSITE}
              subtitle="Website"
            />

            <EmailSignatureEditorNewFieldButton
              disabled={isSignatureLocked}
              onActivate={handleAddNewField}
              type={EmailSignatureContactDetailsFieldTypes.ADDRESS}
              subtitle="Address"
            />

            <EmailSignatureEditorNewFieldButton
              disabled={isSignatureLocked}
              onActivate={handleAddNewField}
              type={EmailSignatureContactDetailsFieldTypes.WHATSAPP}
              subtitle="WhatsApp"
            />

            <EmailSignatureEditorNewFieldButton
              disabled={isSignatureLocked}
              onActivate={handleAddNewField}
              type={EmailSignatureContactDetailsFieldTypes.SIGNAL}
              subtitle="Signal"
            />

            <EmailSignatureEditorNewFieldButton
              disabled={isSignatureLocked}
              onActivate={handleAddNewField}
              type={EmailSignatureContactDetailsFieldTypes.SKYPE}
              subtitle="Skype"
            />

            <EmailSignatureEditorNewFieldButton
              disabled={isSignatureLocked}
              onActivate={handleAddNewField}
              type={EmailSignatureContactDetailsFieldTypes.FAX}
              subtitle="Fax"
            />
          </EmailSignatureEditorPanelNewFieldSection>

          {isSignatureLocked && (
            <EmailSignatureLockLayer
              message={
                'You cannot add any extra fields, please contact your admin if you have any questions'
              }
            />
          )}
        </div>
      </div>
    </>
  );
};

export default EmailSignatureSideEditorContactSection;
